export default function PrivacyPage() {
  return (
    <div className="min-h-screen pt-32 pb-16 bg-charcoal-deep">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-4 mb-6">
          <span className="h-[3px] w-16 bg-thunder" />
          <span className="text-thunder font-display tracking-[0.4em] text-xs">// LEGAL</span>
        </div>
        <h1 className="font-display font-bold text-white text-5xl sm:text-6xl mb-4">PRIVACY POLICY</h1>
        <p className="text-white/50 mb-12">Effective Date: April 12, 2026 — Sonz of Thunder Services LLC ("SOTSVC," "we," "us," or "our")</p>

        <div className="max-w-none space-y-8">

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">1. Information We Collect</h2>
            <p className="text-white/60 mb-3">We collect information you provide directly, including:</p>
            <ul className="list-disc pl-6 text-white/60 space-y-1">
              <li>Name, email address, phone number, and service address when you request a quote or book a service</li>
              <li>Payment information processed securely through Stripe (we do not store card numbers)</li>
              <li>Communications you send us via email, form, or SMS</li>
            </ul>
            <p className="text-white/60 mt-3">We also collect automatically:</p>
            <ul className="list-disc pl-6 text-white/60 space-y-1">
              <li>Log data (IP address, browser type, pages visited) via standard web server logs</li>
              <li>Cookies necessary for site functionality</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">2. SMS Consent and Communications</h2>
            <p className="text-white/60 mb-3">
              SMS messaging is optional and is never required to request a quote or receive service. You opt in only by checking the SMS consent box on our contact, quote, or scheduling forms, or by leaving a voicemail at our business line. Providing your phone number for a quote does not by itself enroll you in text messages.
            </p>
            <p className="text-white/60 mb-3">
              If you opt in, you may receive messages from Sonz of Thunder Services including appointment confirmations, service quotes, missed-call follow-ups, scheduling reminders, and post-service review requests. Message frequency varies. Message and data rates may apply.
            </p>
            <p className="text-white/60 mb-3">
              We do not sell, rent, or share your phone number or SMS opt-in data with third parties or affiliates for marketing or promotional purposes.
            </p>
            <p className="text-white/60">
              To opt out, reply STOP to any message at any time. For help, reply HELP, email privacy@sotsvc.com, or call us. After opting out, you will receive one final confirmation message. We use Twilio to deliver SMS messages; your number and message content are sent to Twilio solely for delivery.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-white/60 space-y-1">
              <li>To schedule, confirm, and perform your cleaning service</li>
              <li>To process payments through Stripe</li>
              <li>To send service reminders, follow-ups, and receipts</li>
              <li>To respond to your questions and support requests</li>
              <li>To comply with legal obligations</li>
            </ul>
            <p className="text-white/60 mt-3">We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">4. Third-Party Service Providers</h2>
            <p className="text-white/60 mb-3">We share data with service providers solely to operate our business:</p>
            <ul className="list-disc pl-6 text-white/60 space-y-2">
              <li><strong>Twilio</strong> — SMS and communication delivery</li>
              <li><strong>Stripe</strong> — Payment processing (PCI-DSS compliant)</li>
              <li><strong>Supabase</strong> — Secure database and authentication infrastructure</li>
            </ul>
            <p className="text-white/60 mt-3">These providers are contractually obligated to protect your data and use it only as directed by us.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">5. Data Retention</h2>
            <p className="text-white/60">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Customer records are typically retained for 3 years after your last service. You may request deletion of your data at any time (see Section 7).
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">6. Data Security</h2>
            <p className="text-white/60">
              We implement industry-standard security measures to protect your data, including encrypted transmission (TLS/HTTPS) and access controls. No system is 100% secure; if you believe your data has been compromised, contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">7. Your Rights (CCPA / GDPR)</h2>
            <p className="text-white/60 mb-3">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 text-white/60 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data ("right to be forgotten")</li>
              <li>Opt out of any marketing communications</li>
              <li>Data portability (receive your data in a machine-readable format)</li>
            </ul>
            <p className="text-white/60 mt-3">
              To exercise any of these rights, email <a href="mailto:privacy@sotsvc.com" className="text-electric hover:text-thunder transition">privacy@sotsvc.com</a>. We will respond within 30 days.
            </p>
            <p className="text-white/60 mt-3">
              <strong>California residents (CCPA):</strong> We do not sell personal information. You have the right to know, delete, and opt out as described above.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">8. Children's Privacy</h2>
            <p className="text-white/60">
              Our services are not directed to individuals under 18. We do not knowingly collect personal data from children.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">9. Changes to This Policy</h2>
            <p className="text-white/60">
              We may update this Privacy Policy from time to time. We will post the updated policy on this page with a revised effective date. Continued use of our services after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="font-display font-bold text-white text-xl tracking-wider mb-3">10. Contact Us</h2>
            <p className="text-white/60">
              Sonz of Thunder Services LLC<br />
              Orlando, Florida<br />
              Email: <a href="mailto:privacy@sotsvc.com" className="text-electric hover:text-thunder transition">privacy@sotsvc.com</a><br />
              Website: <a href="https://sotsvc.com" className="text-electric hover:text-thunder transition">sotsvc.com</a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}
